import MUIAutocomplete from '@mui/material/Autocomplete';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  Typography,
  useTheme,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
  InputLabel,
  Select,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  RecipientMatchCaption,
  RecipientMoreHint,
} from 'src/components/RecipientMatchDisplay';
// icons
import SvgIconStyle from 'src/components/SvgIconStyle';
import { Icon } from '@iconify/react';
import fileFill from '@iconify/icons-eva/file-fill';
import closeFill from '@iconify/icons-eva/close-fill';
// utils
import { fData } from 'src/utils/formatNumber';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
// components
import {
  DesktopDatePicker,
  DesktopTimePicker,
  MobileDatePicker,
  MobileTimePicker,
} from '@mui/x-date-pickers-pro';
import { Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { MIconButton } from 'src/components/@material-extend';
import { varFadeInRight } from 'src/components/animate';
import LazyLoadScript from 'src/components/LazyLoadScript';
import { CarouselControlsArrowsIndex } from 'src/components/carousel';
import useEmblaCarousel from 'embla-carousel-react';
import {
  Schedule,
  Today,
  Sync,
  Upload,
  Clear as ClearIcon,
  OpenInNew,
} from '@mui/icons-material';
import { BUCKET_NAME, S3_BASE_URL } from 'src/config';
import _keys from 'lodash/keys';
import _xor from 'lodash/xor';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import MeasureField, {
  useMeasureFieldUnits,
} from 'src/components/MeasureField';
import { isValidNumber } from 'src/utils/number';
import { extractPlaceData, Weight } from 'careit';
import _isFinite from 'lodash/isFinite';
import _range from 'lodash/range';
import _noop from 'lodash/noop';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import useGetLocaleTextDatePicker from './useGetLocaleTextDatePicker';
import { Edit } from '@mui/icons-material';
import { readFile } from 'src/utils/file';
import { Pets } from '@mui/icons-material';
import TaxExemptBadge from 'src/components/TaxExemptBadge';

// ----------------------------------------------------------------------

const SIZE_INTERVALS = ['xs', 'sm', 'md', 'lg', 'xl'];
const isSizeBelow = (interval, control) =>
  SIZE_INTERVALS.indexOf(interval) <= SIZE_INTERVALS.indexOf(control);

const defaultOnChange = (e) => e.target.value;
const defaultCondition = () => true;
const defaultComponent = ({ error, ...params }) => {
  const rest = { ...params };
  delete rest.key;
  return <TextField helperText={error} error={!!error} {...rest} />;
};
const defaultMapUpdate = ({ state, id, updates }) => ({
  ...state,
  [id]: updates,
});

export const CarouselItem = ({ item, onClick, width, height, objectFit }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (typeof item === 'string') {
      setUrl(`${S3_BASE_URL}/${BUCKET_NAME}/${item}`);
    } else if (item?.url && typeof item.url === 'string') {
      setUrl(`${S3_BASE_URL}/${BUCKET_NAME}/${item.url}`);
    } else {
      setUrl(URL.createObjectURL(item.file));
    }
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [item]);

  return (
    <ButtonBase onClick={onClick} sx={{ width, height }}>
      <Box
        component="img"
        sx={{ objectFit: objectFit ?? 'cover' }}
        src={url}
        width="100%"
        height="100%"
      />
    </ButtonBase>
  );
};

export const formatYupErrors = (yupErrors) => {
  const errors_raw = yupErrors
    .filter(({ errors }) => errors.length > 0)
    .map(({ errors, path }) => [errors[0], path]);
  const errors = {};
  for (let i = 0, e = errors_raw.length; i < e; i++) {
    const [error, path] = errors_raw[i];
    errors[path] = error;
  }

  return errors;
};

/**
 * @deprecated
 * This is an old form utility which has been deprecated due to it's unecessary
 * complexity. In any new forms, please use the new form system which is
 * currently being used in the inspection forms among other places.
 */
export default function useForm({
  noClose = false,
  handleSave,
  form,
  title,
  defaultState: _defaultState,
  schema,
  onClose = _noop,
  dialogContentSx = {},
  descriptionText,
}) {
  const { t } = useTranslation('translation');
  // apply custom default state mixin
  const defaultState = { ..._defaultState, _step: 0, _steps: 0 };

  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useState(defaultState ?? {});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);

  // Initialize default states for form items
  useEffect(() => {
    if (!form || form.length === 0) return;

    const updates = {};
    let hasUpdates = false;

    const processItems = (items) => {
      items.forEach((item) => {
        if (item.items && Array.isArray(item.items)) {
          processItems(item.items);
        } else if (item.steps && Array.isArray(item.steps)) {
          item.steps.forEach((step) => {
            if (step.items && Array.isArray(step.items)) {
              processItems(step.items);
            }
          });
        } else if (
          item.id &&
          state[item.id] === undefined &&
          item.defaultState !== undefined
        ) {
          updates[item.id] = item.defaultState;
          hasUpdates = true;
        }
      });
    };

    processItems(form);

    if (hasUpdates) {
      setState((prevState) => ({ ...prevState, ...updates }));
    }
  }, [form, state]);

  const handleOpen = (e, value) => {
    setOpen(true);
    if (value) {
      setState({ ...value, _step: 0, _steps: 0 });
    }
  };
  const handleClose = () => {
    setOpen(false);
    setState(defaultState ?? {});
    setErrors({});
    onClose();
  };

  const handleDoSave = () => {
    setLoading(true);
    setErrors({});
    schema
      .validate(state, { abortEarly: false })
      .then(handleSave)
      .then(() => {
        setState(defaultState ?? {});
        handleClose();
      })
      .catch((err) => {
        // If the error is not from validation, but rather from "handleSave" it
        // will not have an inner attribute. We should handle this like any
        // other error.
        if (!err.inner) {
          console.error(err);
          enqueueSnackbar(t('form.error-try-again'), {
            variant: 'error',
          });
          Sentry.withScope((scope) => {
            scope.setContext('feedbackDialog', { hide: true });
            Sentry.captureException(err);
          });
          return;
        }

        const errors = formatYupErrors(err.inner);
        setErrors(errors);
      })
      .finally(() => setLoading(false));
  };

  const renderItem = (item) => {
    // Get the current value or default value without causing state update during render
    const currentValue =
      state[item.id] !== undefined ? state[item.id] : item.defaultState;

    const onChange = (...params) =>
      setState((state) =>
        (item.mapUpdate ?? defaultMapUpdate)({
          state,
          id: item.id,
          updates: (item.onChange ?? defaultOnChange)(...params),
        })
      );

    const ComponentToRender = item.component ?? defaultComponent;
    const componentProps = {
      key: item.id,
      error: errors[item.id],
      fullWidth: true,
      margin: 'normal',
      label: item.name,
      state,
      errors,
      value: currentValue ?? '',
      setValue: (id, value) => {
        setState((prv) => ({ ...prv, [id]: value }));
      },
      onChange,
      ...(item.mapState ?? (() => ({})))(state),
      ...(typeof item.params === 'function'
        ? item.params(currentValue ?? '')
        : item.params),
    };
    const component = React.createElement(ComponentToRender, componentProps);

    return (item.condition ?? defaultCondition)(state) ? (
      component
    ) : (
      <Box display="none">{component}</Box>
    );
  };

  const renderItemOrSection = (item, i) => {
    if (!(item && _keys(item).length > 1)) {
      return <Box></Box>;
    }
    return {
      undefined: renderItem,
      stepper: (section) => {
        let _indexCounter = 0;
        const steps = section.steps.map((step) => {
          // Recursively search the form tree to see if any children have
          // errors.
          const findError = (node) => {
            if (
              errors[node?.id] ||
              Object.keys(errors).some((key) => key.startsWith(node?.id))
            ) {
              return true;
            }

            if (Array.isArray(node.items)) {
              return node.items.some(findError);
            }

            return false;
          };
          const containsError = findError(step);

          // If step.condition exists and returns false, then we should hide
          // this step.
          //
          // We cannot simply return `null` here as it interferes with react's
          // component update expectations, so we set `hidden=true` on <Step />.
          //
          // However, by hiding the step, MUI does not automattically
          // recalculate the indexes of each step to skip the hidden step. To
          // fix this, we manually compute the step indexes ourselves.
          //
          // KNOWN ISSUES:
          //  This will cause strange behaviour if a previous step is hidden
          //  after a change in a later step. This is, of course, generally bad
          //  behaviour even if we handled the issue gracefully. Please avoid
          //  doing this, and if possible avoid useForm altogether.
          const hidden = step.condition ? !step.condition(state) : false;

          let index = _indexCounter;
          if (hidden) {
            // The index must be unique, so we allocate indexes beyond
            // steps.length for hidden steps.
            index = section.steps.length + index;
          } else {
            _indexCounter += 1;
          }

          return (
            <Step key={step.title} hidden={hidden} index={index}>
              <StepLabel error={containsError}>{step.title}</StepLabel>
              <StepContent>
                {step.items.map((item, i) => (
                  <div key={item.id || i}>{renderItemOrSection(item, i)}</div>
                ))}
              </StepContent>
            </Step>
          );
        });

        return (
          <Stepper
            orientation={section.vertical ? 'vertical' : 'horizontal'}
            activeStep={state._step}
          >
            {(() => {
              if (section.steps.length - 1 !== state._steps) {
                setState({ ...state, _steps: section.steps.length - 1 });
              }
            })()}
            {steps}
          </Stepper>
        );
      },
      horizontal: (section) => {
        const rendered = (
          <>
            {section.title && (
              <Typography
                color="text.secondary"
                variant="caption"
                margin="10px"
              >
                {section.title}
              </Typography>
            )}
            <Box
              key={i}
              display="flex"
              sx={{
                flexDirection: section.collapseSize
                  ? SIZE_INTERVALS.reduce((acc, x) => {
                      acc[x] = isSizeBelow(x, section.collapseSize)
                        ? 'column'
                        : 'row';
                      return acc;
                    }, {})
                  : 'row',
              }}
              justifyContent={
                section.compact
                  ? section.left
                    ? 'start'
                    : 'center'
                  : 'space-evenly'
              }
            >
              {section.items.map((item, i) => (
                <div key={item.id || i}>{renderItem(item)}</div>
              ))}
            </Box>
            {errors[section.id] && (
              <Typography color="error" variant="caption" margin="10px">
                {errors[section.id]}
              </Typography>
            )}
          </>
        );

        return section.condition && !section.condition(state) ? (
          <Box display="none">{rendered}</Box>
        ) : (
          rendered
        );
      },
    }[item.section](item);
  };

  const setFieldState = (id, value) => {
    setState({ ...state, [id]: value });
  };

  const updateBulkState = (updates) => setState({ ...state, ...updates });

  return {
    handleOpen,
    handleClose,
    form: () => (
      <Dialog
        open={open}
        onClose={noClose ? undefined : handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            pb: 2,
          }}
        >
          {title}

          {descriptionText && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
              }}
            >
              {descriptionText}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={dialogContentSx}>
          {form.map((item, i) => (
            <div key={item.id || i}>{renderItemOrSection(item, i)}</div>
          ))}
        </DialogContent>
        <DialogActions>
          {state._step !== 0 && (
            <Button onClick={handleClose} sx={{ mt: 1, mr: 1 }}>
              {t('form.actions.cancel')}
            </Button>
          )}

          <Box flex="1" />

          <Box
            position="fixed"
            left="calc(50vw - 100px)"
            width="200px"
            display="flex"
            justifyContent="center"
          >
            {Object.keys(errors).length > 0 && (
              <Typography marginRight="15px" color="error">
                {t('form.fix-all-errors')}
              </Typography>
            )}
          </Box>

          <Button
            onClick={() =>
              state._step === 0
                ? handleClose()
                : setState({ ...state, _step: state._step - 1 })
            }
            sx={{ mt: 1, mr: 1 }}
            disabled={loading}
          >
            {state._step === 0
              ? t('form.actions.cancel')
              : t('form.actions.back')}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={() => {
              state._step === state._steps
                ? handleDoSave()
                : setState({ ...state, _step: state._step + 1 });
            }}
            sx={{ mt: 1, mr: 1 }}
            loading={loading}
          >
            {state._step === state._steps
              ? t('form.actions.finish')
              : t('form.actions.continue')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    ),
    state,
    setState,
    open,
    setFieldState,
    errors,
    updateBulkState,
  };
}

// ----------------------------------------------------------------------

export function makeMultiline(rows, maxLen) {
  return (value) => ({
    multiline: true,
    rows,
    inputProps: { maxLength: maxLen },
    helperText: `${value?.length ?? 0} / ${maxLen}`,
  });
}

export function checkboxFormOnChange(e) {
  return e.target.checked;
}

export function CheckboxFormComponent({
  label,
  key,
  error,
  value,
  checked,
  ...params
}) {
  return (
    <FormControl margin="normal" error={!!error} key={key}>
      <FormControlLabel
        control={<Checkbox {...params} checked={Boolean(value ?? checked)} />}
        label={<Typography color="text.secondary">{label}</Typography>}
      />
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}

export function radioGroupOnChange(e) {
  return e.target.checked;
}

export const RadioGroupComponent = (items, Header = null) =>
  function RadioGroupComponentImpl({
    label,
    key,
    error,
    fullWidth,
    ...params
  }) {
    return (
      <FormControl
        margin="normal"
        error={!!error}
        key={key}
        fullWidth={fullWidth}
      >
        <FormLabel component="legend">{label}</FormLabel>
        {Header}
        <RadioGroup aria-label={label} name="radio-buttons-group" {...params}>
          {items.map((item) => (
            <FormControlLabel
              value={item.value}
              key={item.value}
              control={<Radio />}
              label={item.label}
              disabled={item.disabled ?? false}
            />
          ))}
        </RadioGroup>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  };

export function ToggleButtonComponent({
  label,
  key,
  onChange,
  value,
  ...params
}) {
  return (
    <Button
      key={key}
      onClick={() => onChange({ target: { checked: !value } })}
      size="small"
      color={value ? 'primary' : 'inherit'}
      variant="contained"
      {...params}
      sx={{ ...params.sx, minWidth: '25px' }}
    >
      {label}
    </Button>
  );
}

export function toggleButtonGroupOnChange(_, value) {
  return value;
}

export function createToggleButtonGroup(items) {
  return function ToggleButtonGroupImpl({ key, error, label, ...params }) {
    const { t } = useTranslation('translation');
    const handleChangeInMobile = (a, b) => {
      const newValues = _xor(params.value, [b]);
      params.onChange(a, newValues);
    };

    return (
      <span key={key}>
        <Typography margin="5px" color={error ? 'error' : 'text.secondary'}>
          {label}
        </Typography>

        <Grid container spacing={1}>
          {items.map((item) => (
            <Grid size={{ xs: 6, sm: 3, md: 3 }} key={item.id}>
              <ToggleButton
                key={item.id}
                value={item.id}
                fullWidth
                onChange={handleChangeInMobile}
                selected={params.value.includes(item.id)}
                color="primary"
              >
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  paddingTop="15px"
                >
                  {item.image.endsWith('.svg') ? (
                    <SvgIconStyle
                      src={item.image}
                      sx={{ width: 50, height: 50 }}
                      color={error ? 'error' : 'inherit'}
                    />
                  ) : (
                    <img
                      src={item.image}
                      style={{ width: 50, height: 50 }}
                      color={error ? 'error' : 'inherit'}
                      alt={t('form.icon-for', { title: item.title })}
                    />
                  )}
                  <Typography
                    margin="15px"
                    textAlign="center"
                    color={error ? 'error' : 'text.secondary'}
                  >
                    {item.title}
                  </Typography>
                </Box>
              </ToggleButton>
            </Grid>
          ))}
        </Grid>

        {error && (
          <Typography margin="5px" color="error" variant="body2">
            {error}
          </Typography>
        )}
      </span>
    );
  };
}

export function foodCategoryOnChange(_, value) {
  return value;
}

export function createFoodCategoryGroup(items, { fullSize = false } = {}) {
  return function FoodCategoryGroupImpl({
    key,
    error,
    label,
    defaultUnit,
    ...params
  }) {
    const { t } = useTranslation('translation');
    const handleInputChange = (name) => (value) => {
      params.onChange(name, {
        ...params.value,
        [name]: value,
      });
    };

    const { WEIGHT_UNITS } = useMeasureFieldUnits();
    // users can click the food category to "toggle" it
    //
    // if the food run is inactive (no value) -> activate this category and set the value to 0
    // if the food run is active and the value is 0 -> deactivate this category and clear the value
    // otherwise pass
    const handleToggle = (id) => () => {
      let value = _get(params, `value[${id}].value`);

      if (parseFloat(value) === 0) {
        // remove `id` key from `value` object
        const clonedValue = _cloneDeep(params.value);
        delete clonedValue[id];
        params.onChange(id, clonedValue);
      } else if (!isValidNumber(value)) {
        handleInputChange(id)({
          value: 0,
          unit: defaultUnit,
        });
      }
    };

    return (
      <span key={key}>
        <Typography margin="5px" color={error ? 'error' : 'text.secondary'}>
          {label}
        </Typography>
        <Box>
          <Grid container spacing={1}>
            {items.map((item) => {
              const selected = _get(params.value, `${item.id}.value`, -1) >= 0;
              return (
                <Grid
                  key={item.id}
                  size={{ xs: 12, sm: 6, md: 4 }}
                  {...(fullSize ? { lg: 3 } : {})}
                >
                  <Box
                    width="100%"
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 2 }}
                    key={item.id}
                    className="food-category-item"
                    style={{
                      background: selected ? '#f4fbf7' : null,
                      cursor: 'pointer',
                    }}
                    onClick={handleToggle(item.id)}
                  >
                    <SvgIconStyle
                      src={item.image}
                      sx={{ width: 50, height: 50 }}
                      color={
                        error ? 'error' : selected ? 'primary' : 'disabled'
                      }
                    />
                    <Typography
                      margin="15px"
                      textAlign="center"
                      color={
                        error
                          ? 'error'
                          : selected
                            ? 'primary'
                            : 'text.secondary'
                      }
                      fontSize={14}
                      height={40}
                    >
                      {item.title}
                    </Typography>

                    <MeasureField
                      label={t('common.weight')}
                      units={WEIGHT_UNITS}
                      value={_get(params, `value[${item.id}]`)}
                      onChange={handleInputChange(item.id)}
                      style={{ width: '80%' }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        {error && (
          <Typography margin="5px" color="error" variant="body2">
            {error}
          </Typography>
        )}
      </span>
    );
  };
}

export function MeasureFieldComponent({ ...params }) {
  const { WEIGHT_UNITS } = useMeasureFieldUnits();
  return (
    <MeasureField
      units={WEIGHT_UNITS.filter((item) => item.value !== 'meals')}
      style={{ width: '100%' }}
      {...params}
      onChange={(value) => {
        params.onChange(params.key, {
          ...value,
        });
      }}
    />
  );
}

function onChangeIdentity(val) {
  return val;
}

export const dateTimeOnChange = onChangeIdentity;

export function DateTimeComponent({ key, error, ...params }) {
  return (
    <Box key={key} marginTop="15px" mx="10px" display="flex">
      <DesktopDatePicker
        {...params}
        onChange={(dateJs) => {
          const date = moment(dateJs);
          const newDate = moment(params.value)
            .year(date.year())
            .month(date.month())
            .date(date.date())
            .toDate();
          params.onChange(newDate);
        }}
        label={params.label + ' (Date)'}
        dateRangeIcon={<Today color="disabled" />}
        timeIcon={<Schedule color="disabled" />}
        renderInput={(params) => (
          <TextField helperText={error} error={!!error} {...params} />
        )}
      />

      <Box width="20px" />

      <DesktopTimePicker
        {...params}
        onChange={(dateJs) => {
          const date = moment(dateJs);
          const newDate = moment(params.value)
            .hour(date.hour())
            .minute(date.minute())
            .toDate();
          params.onChange(newDate);
        }}
        label={params.label + ' (Time)'}
        dateRangeIcon={<Today color="disabled" />}
        timeIcon={<Schedule color="disabled" />}
        renderInput={(params) => (
          <TextField helperText={error} error={!!error} {...params} />
        )}
      />
    </Box>
  );
}

export const dateOnChange = onChangeIdentity;

export const DateComponent = React.forwardRef(
  ({ key, error, containerSx = {}, ...params }, ref) => {
    const localeText = useGetLocaleTextDatePicker();

    return (
      <Box key={key} margin={1} marginTop="15px" sx={containerSx} ref={ref}>
        <MobileDatePicker
          {...params}
          localeText={localeText}
          value={params.value ? new Date(params.value) : null}
          dateRangeIcon={<Today color="disabled" />}
          closeOnSelect
          slotProps={{
            textField: {
              helperText: error,
              error: !!error,
            },
          }}
        />
      </Box>
    );
  }
);
DateComponent.displayName = 'DateComponent';

export const timeOnChange = onChangeIdentity;

export const TimeComponent = React.forwardRef(
  ({ key, error, ...params }, ref) => {
    const localeText = useGetLocaleTextDatePicker();

    return (
      <Box key={key} margin={1} marginTop="15px" ref={ref}>
        <MobileTimePicker
          {...params}
          localeText={localeText}
          dateRangeIcon={<Today color="disabled" />}
          slotProps={{
            textField: {
              helperText: error,
              error: !!error,
            },
          }}
        />
      </Box>
    );
  }
);
TimeComponent.displayName = 'TimeComponent';

// ----------------------------------------------------------------------

export function SelectAddressComponent({
  key,
  error,
  value,
  onChange,
  label,
  enableMapPicking = false,
  ...props
}) {
  const theme = useTheme();
  const selectedValue = useMemo(() => value || {}, [value]);
  const { onLoad, handleChangePlace } = useAutoComplete({
    onChange,
    afterSelect: (placeData) =>
      setMapOptions({
        ...mapOptions,
        zoom: 16,
        center: { lat: placeData.lat, lng: placeData.lng },
      }),
  });

  const [mapOptions, setMapOptions] = useState({
    zoom: 2,
    minZoom: 2,
    maxZoom: 16,
    center:
      selectedValue.lat && selectedValue.lng
        ? { lat: selectedValue.lat, lng: selectedValue.lng }
        : { lat: 38.685, lng: -115.234 },
    controlSize: 24,
    disableDefaultUI: true,
    gestureHandling: enableMapPicking ? 'greedy' : 'none',
    zoomControl: enableMapPicking,
    mapTypeControl: true,
  });

  useEffect(() => {
    if (!selectedValue.lng || !selectedValue.lat) return;

    if (
      mapOptions.center.lat === selectedValue.lat &&
      mapOptions.center.lng === selectedValue.lng &&
      mapOptions.zoom === 16
    )
      return;

    setMapOptions({ ...mapOptions, zoom: 16, center: selectedValue });
  }, [selectedValue, mapOptions]);

  const performReverseGeocoding = useCallback(
    async (lat, lng) => {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await geocoder.geocode({ location: { lat, lng } });

        if (result.results && result.results[0]) {
          const place = result.results[0];
          place.geometry.location.lat = lat;
          place.geometry.location.lng = lng;

          const placeData = extractPlaceData(place);
          onChange(placeData);
        }
      } catch (err) {
        console.error('Reverse geocoding error:', err);
        // Fallback: set coordinates without full address data
        onChange({
          lat,
          lng,
          formattedAddress: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        });
      }
    },
    [onChange]
  );

  const handleMapClick = useCallback(
    async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Update map zoom
      setMapOptions((prev) => ({
        ...prev,
        zoom: 16,
      }));

      // Reverse geocode to get address details
      await performReverseGeocoding(lat, lng);
    },
    [performReverseGeocoding]
  );

  const handleMarkerDragEnd = useCallback(
    async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Reverse geocode to get address details
      await performReverseGeocoding(lat, lng);
    },
    [performReverseGeocoding]
  );

  return (
    <Box height="50vh" {...props} key={key}>
      <LazyLoadScript>
        <GoogleMap
          options={mapOptions}
          mapContainerStyle={{
            width: '100%',
            height: '100%',
            border: error && '3px solid red',
            borderRadius: 4,
          }}
          onClick={enableMapPicking ? handleMapClick : undefined}
        >
          {/**
           * Key is added to autocomplete to re-render the input field when value was set manually using `setFieldState`
           * Otherwise the map shows the location of the updated value but the input has empty value
           **/}
          <Autocomplete
            onLoad={onLoad}
            onPlaceChanged={handleChangePlace}
            key={
              selectedValue.name || selectedValue.formattedAddress || 'empty'
            }
          >
            <OutlinedInput
              type="text"
              size="small"
              placeholder={label}
              sx={{
                position: 'absolute',
                top: 8,
                left: 0,
                right: 0,
                width: { xs: '90%', sm: '50%' },
                mx: 'auto',
                backgroundColor: theme.palette.common.white,
                zIndex: theme.zIndex.appBar,
                transition: theme.transitions.create(['box-shadow', 'width'], {
                  easing: theme.transitions.easing.easeInOut,
                  duration: theme.transitions.duration.shorter,
                }),
                '&.Mui-focused': {
                  width: { xs: '95%', sm: 320 },
                  boxShadow: theme.customShadows.z8,
                },
                '& fieldset': {
                  borderWidth: `1px !important`,
                  borderColor: `${theme.palette.grey[500_32]} !important`,
                },
              }}
              error={!!error}
              defaultValue={
                selectedValue.name || selectedValue.formattedAddress || ''
              }
            />
          </Autocomplete>
          {mapOptions.zoom === 16 && (
            <Marker
              position={mapOptions.center}
              draggable={enableMapPicking}
              onDragEnd={enableMapPicking ? handleMarkerDragEnd : undefined}
              icon={{
                path: 'M8 0C3.61277632-.00021937.04387223 3.53299568 0 7.92 0 13.4 7.05 19.5 7.35 19.76c.374224.3200877.925776.3200877 1.3 0C9 19.5 16 13.4 16 7.92 15.9561278 3.53299568 12.3872237-.00021937 8 0zm0 11c-1.9329966 0-3.5-1.5670034-3.5-3.5C4.5 5.56700338 6.0670034 4 8 4s3.5 1.56700338 3.5 3.5c0 .9282577-.3687489 1.8184964-1.0251263 2.4748737C9.8184964 10.6312511 8.9282577 11 8 11z',
                fillColor: theme.palette.error.main,
                fillOpacity: 1.0,
                strokeWeight: 0,
                scale: 1.5,
              }}
            />
          )}
        </GoogleMap>
      </LazyLoadScript>
      {error && (
        <Typography margin="5px" variant="body2" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
}

export function SelectAddressComponentPopup(props) {
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);

  return (
    <>
      <TextField
        onClick={() => setOpen(true)}
        label={props.label}
        fullWidth
        required={props.required}
        error={!!props.error}
        helperText={props.error}
        margin="normal"
        sx={{ mx: 1 }}
        value={props.value?.formattedAddress || ''}
        InputLabelProps={{ shrink: !!props.value?.formattedAddress }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <DialogTitle>{props.label}</DialogTitle>

        <DialogContent>
          <SelectAddressComponent {...props} width="750px" />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={() => setOpen(false)}>
            {t('form.actions.done')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export const selectAddressOnChange = onChangeIdentity;

const useAutoComplete = ({ onChange, afterSelect = _noop }) => {
  const [autocomplete, setAutocomplete] = useState(null);

  const onLoad = (autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const handleChangePlace = useCallback(async () => {
    if (autocomplete === null) return;

    const place = autocomplete.getPlace();
    if (!place.place_id) {
      const fallbackQuery =
        place?.name || place?.formatted_address || place?.vicinity;
      if (!fallbackQuery) return;

      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await geocoder.geocode({ address: fallbackQuery });
        const resolvedPlace = result?.results?.[0];
        if (!resolvedPlace) return;
        const location = resolvedPlace?.geometry?.location;
        if (
          !location ||
          typeof location.lat !== 'function' ||
          typeof location.lng !== 'function'
        ) {
          return;
        }

        // Geocoder results often lack `name`; provide a fallback so downstream
        // payloads don't send an empty place value.
        if (!resolvedPlace.name && resolvedPlace.formatted_address) {
          resolvedPlace.name = resolvedPlace.formatted_address;
        }

        resolvedPlace.geometry.location.lat = location.lat();
        resolvedPlace.geometry.location.lng = location.lng();

        const placeData = extractPlaceData(resolvedPlace);
        onChange(placeData);
        afterSelect(placeData);
      } catch (err) {
        console.error('Fallback geocode error:', err);
      }
      return;
    }

    // autocomplete API stores these as lazy getters
    place.geometry.location.lat = place.geometry.location.lat();
    place.geometry.location.lng = place.geometry.location.lng();

    const placeData = extractPlaceData(place);

    onChange(placeData);
    afterSelect(placeData);
  }, [onChange, afterSelect, autocomplete]);
  return { setAutocomplete, onLoad, handleChangePlace };
};

export function AddressInputWithoutMap({
  key,
  error,
  value,
  onChange,
  label,
  ...props
}) {
  const { onLoad, handleChangePlace } = useAutoComplete({ onChange });

  return (
    <Box key={key} {...props}>
      <LazyLoadScript>
        {/**
         * Key is added to autocomplete to re-render the input field when value was set manually using `setFieldState`
         * Otherwise the map shows the location of the updated value but the input has empty value
         **/}
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={handleChangePlace}
          key={value.name || value.formattedAddress}
        >
          <TextField
            type="text"
            label={label}
            placeholder={label}
            error={!!error}
            fullWidth
            defaultValue={value.formattedAddress || value.name}
            {...props}
          />
        </Autocomplete>
      </LazyLoadScript>
      {error && (
        <Typography margin="5px" variant="body2" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
}

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': {
    opacity: 0.72,
    cursor: 'pointer',
  },
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
    flexDirection: 'row',
  },
}));

export const FilePreviewList = ({
  files,
  hasFile,
  handleRemoveFile,
  text,
  includeLink = false,
  displayEditBtn = false,
}) => {
  const { t } = useTranslation('translation');
  return (
    <Box>
      {displayEditBtn ? (
        <Button endIcon={<Edit />}>{t('form.update-attachments')}</Button>
      ) : null}
      <List disablePadding sx={{ ...(hasFile && { my: 5 }) }}>
        <AnimatePresence>
          {files.map((item, id) => {
            const { file, name } = item;
            return (
              <ListItem
                key={id}
                component={motion.div}
                {...varFadeInRight}
                sx={{
                  my: 1,
                  py: 0.5,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `solid 1px ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                }}
              >
                <ListItemIcon>
                  <Icon icon={fileFill} width={32} height={32} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    file?.name ??
                    name ??
                    t('form.file-numbered', {
                      name: text || t('form.image'),
                      number: id + 1,
                    })
                  }
                  secondary={file?.size ? fData(file.size) : ''}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                />
                {includeLink && typeof item == 'string' && (
                  <ListItemSecondaryAction>
                    <MIconButton
                      edge="end"
                      size="small"
                      onClick={() => window.open(item)}
                    >
                      <OpenInNew />
                    </MIconButton>
                  </ListItemSecondaryAction>
                )}
                {handleRemoveFile && (
                  <ListItemSecondaryAction>
                    <MIconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveFile(id)}
                    >
                      <Icon icon={closeFill} />
                    </MIconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
        </AnimatePresence>
      </List>
    </Box>
  );
};

export function UploadImagesComponent({
  label,
  value,
  onChange: setFiles,
  sx,
  error,
  width = 250,
  height = 250,
  objectFit,
  displayLabel,
  maxFiles = 0,
  maxSize,
  accept = undefined,
  removeDefaultAcceptedFiles = false,
  documentLayout = false,
  description,
  fileListText,
  disabled,
  handleFileCountExceed = _noop,
  displayEditBtnIndocs = false,
  noDialog = false,
  ...other
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('translation');
  const files = value.filter((item) => item);
  const defaultAcceptedFiles = {
    'image/jpeg': [],
    'image/jpg': [],
    'image/png': [],
    'image/webp': [],
  };
  const acceptedFiles = removeDefaultAcceptedFiles
    ? accept
    : { ...defaultAcceptedFiles, ...accept };
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const hasFile = files.length > 0;

  const handleDrop = useCallback(
    (acceptedFiles) =>
      (async () => {
        try {
          let allFiles = [];
          for (let i = 0, e = acceptedFiles.length; i < e; i++) {
            const file = acceptedFiles[i];
            let data = await readFile(file, true);
            allFiles.push({ file, data });
          }
          setFiles(files.concat(allFiles));
        } catch (err) {
          Sentry.withScope((scope) => {
            scope.setContext('file_metadata', {
              acceptedFiles: JSON.stringify(
                acceptedFiles.map((f) => ({
                  size: f.size,
                  type: f.type,
                  name: f.name,
                  path: f.path,
                  relativePath: f.relativePath,
                }))
              ),
              message: err?.message,
              href: window.location?.href,
            });
            scope.captureException(new Error('Error while transforming image'));
          });
          enqueueSnackbar(t('form.photo-processing-error'), {
            variant: 'error',
          });
        }
      })(),
    [files, setFiles]
  );

  const handleRemoveFile = (id) => {
    setFiles(files.filter((_, i) => i !== id));
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept: acceptedFiles,
      onDrop: handleDrop,
      maxFiles,
      validator: () => {
        // If maxFiles is set and current files exceed or equal maxFiles, reject
        if (maxFiles > 0 && files.length >= maxFiles) {
          handleFileCountExceed();
          return {
            code: 'too-many-files',
            message: t('form.max-files-allowed', { count: maxFiles }),
          };
        }
        return null;
      },
      maxSize,
      minSize: 0.1,
      onDropRejected: ([{ errors }]) => {
        if (errors[0].code === 'file-too-large' && maxSize) {
          enqueueSnackbar(
            t('form.file-size-limit', {
              size: Math.round(maxSize / 1024 / 1024),
            }),
            { variant: 'error' }
          );
        }
        if (errors[0].code === 'file-too-small') {
          enqueueSnackbar(t('form.validation.file-empty'), {
            variant: 'error',
          });
        }
        if (errors[0].code === 'file-invalid-type') {
          enqueueSnackbar(errors[0].message, { variant: 'error' });
        }
      },
    });

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on('select', handleSelect);
    emblaApi.on('reInit', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('reInit', handleSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !hasFile) {
      return;
    }

    const intervalId = window.setInterval(() => {
      emblaApi.scrollNext();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [emblaApi, hasFile]);

  const component = (
    <Box sx={{ width: '100%' }} {...other}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            borderColor: 'primary.light',
            bgcolor: 'primary.lighter',
          }),
        }}
      >
        <input {...getInputProps()} />

        <Box
          component="img"
          alt={t('form.select-file-alt')}
          src="/static/illustrations/illustration_upload.svg"
          sx={{ height: 160 }}
        />

        <Box
          sx={{
            p: 3,
            ml: { md: 2 },
          }}
        >
          <Typography color="text.primary" gutterBottom variant="h5">
            {t('upload-file.drop-or-select-file')}
          </Typography>

          {label ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {label}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('upload-file.drop-file-here-or')}&nbsp;
              <Link underline="always">{t('upload-file.browse')}</Link>
              &nbsp;
              {t('upload-file.thorough-your-machine')}
            </Typography>
          )}
        </Box>
      </DropZoneStyle>

      <FilePreviewList
        files={files}
        hasFile={hasFile}
        handleRemoveFile={handleRemoveFile}
        text={fileListText}
      />

      {hasFile && !noDialog && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            '& > *': { ml: 1.5 },
          }}
        >
          <Button onClick={() => setFiles([])} sx={{ mr: 1.5 }}>
            {t('upload-file.remove-all')}
          </Button>
          <Button variant="contained" onClick={() => setOpen(false)}>
            {t('upload-file.add')}
          </Button>
        </Box>
      )}
    </Box>
  );

  if (noDialog) {
    return (
      <>
        {displayLabel ? (
          <FormLabel component="legend">{label}</FormLabel>
        ) : null}
        {description && (
          <Typography display="block" variant="caption" color="textSecondary">
            {description}
          </Typography>
        )}
        {component}

        {error && (
          <Typography variant="body2" margin="5px" color="error">
            {error}
          </Typography>
        )}
      </>
    );
  }

  return (
    <>
      {displayLabel ? <FormLabel component="legend">{label}</FormLabel> : null}
      {description && (
        <Typography display="block" variant="caption" color="textSecondary">
          {description}
        </Typography>
      )}
      {files.length > 0 ? (
        <>
          {documentLayout ? (
            <ButtonBase
              sx={{ width, display: 'block' }}
              onClick={() => setOpen(true)}
            >
              <FilePreviewList
                files={files}
                hasFile={false}
                text={fileListText}
                displayEditBtn={!open && displayEditBtnIndocs}
              />
            </ButtonBase>
          ) : (
            <Card
              sx={{
                width,
                height,
                border: error && '2px solid red',
                mt: 1,
                position: 'relative',
                overflow: 'hidden',
                ...sx,
              }}
            >
              <Box
                ref={emblaRef}
                sx={{ overflow: 'hidden', width: '100%', height: '100%' }}
              >
                <Box sx={{ display: 'flex', height: '100%' }}>
                  {files.map((item) => (
                    <Box
                      key={item.title || item.file}
                      sx={{
                        flex: '0 0 100%',
                        minWidth: 0,
                        height: '100%',
                      }}
                    >
                      <CarouselItem
                        item={item}
                        onClick={() => setOpen(true)}
                        width={width}
                        height={height}
                        objectFit={objectFit}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              <CarouselControlsArrowsIndex
                index={currentIndex}
                total={files.length}
                onNext={() => emblaApi?.scrollNext()}
                onPrevious={() => emblaApi?.scrollPrev()}
              />
            </Card>
          )}
        </>
      ) : (
        <Button
          disabled={disabled}
          startIcon={<Upload />}
          onClick={() => setOpen(true)}
          variant="outlined"
          sx={{ mt: 2, mb: 1 }}
        >
          {displayLabel && label ? label : t('upload-file.upload-files')}
        </Button>
      )}

      {error && (
        <Typography variant="body2" margin="5px" color="error">
          {error}
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent>{component}</DialogContent>
      </Dialog>
    </>
  );
}

export const uploadImagesOnChange = onChangeIdentity;

export function UploadFileComponent({
  label,
  value: file,
  onChange: setFile,
  sx,
  error,
  width = '100%',
  height = '40vh',
  accept,
  preview = () => <></>,
  maxSize,
  onDropRejected = _noop,
  showImage = true,
  ...other
}) {
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);

  const hasFile = !!file;

  const normalizedAccept = useMemo(() => {
    const buildAcceptMap = (entries) => {
      if (!entries || entries.length === 0) {
        return undefined;
      }

      const map = {};
      let lastMime = null;

      entries.forEach((raw) => {
        if (typeof raw !== 'string') {
          return;
        }

        const item = raw.trim();
        if (!item) {
          return;
        }

        if (item.includes('/')) {
          lastMime = item;
          if (!map[lastMime]) {
            map[lastMime] = [];
          }
        } else if (item.startsWith('.')) {
          const targetMime = lastMime || 'application/octet-stream';
          if (!map[targetMime]) {
            map[targetMime] = [];
          }
          map[targetMime] = [...map[targetMime], item];
        }
      });

      return Object.keys(map).length > 0 ? map : undefined;
    };

    if (!accept) {
      return accept;
    }

    if (typeof accept === 'string') {
      const tokens = accept
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean);

      if (tokens.length <= 1 && !tokens[0]?.startsWith('.')) {
        return accept;
      }

      return buildAcceptMap(tokens);
    }

    if (Array.isArray(accept)) {
      return buildAcceptMap(accept);
    }

    return accept;
  }, [accept]);

  const handleDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) {
      return;
    }

    let data = await readFile(file);
    setFile({ file, data, dataUri: data });
  };

  const handleRemoveFile = () => setFile();

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept: normalizedAccept,
      multiple: false,
      onDrop: handleDrop,
      maxSize,
      onDropRejected,
    });
  const rest = { ...other };
  delete rest.fullWidth;
  delete rest.key;
  const Preview = preview;
  return (
    <>
      <Box
        sx={{
          width,
          height,
          border: (t) =>
            error ? '1px solid red' : `1px dashed ${t.palette.divider}`,
          borderRadius: 1,
          backgroundColor: (t) => t.palette.grey[100],
          ...sx,
        }}
      >
        {hasFile ? (
          <Preview file={file} data={file.data} />
        ) : (
          <>
            {showImage ? (
              <ButtonBase
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
                onClick={() => setOpen(true)}
              >
                <Box
                  component="img"
                  alt={t('form.select-file-alt')}
                  src="/static/illustrations/illustration_upload.svg"
                  sx={{ objectFit: 'cover' }}
                  height={height / 2}
                />

                <Box>
                  <Typography textAlign="start" variant="h4" fontWeight="bold">
                    {t('upload-file.select-files')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('upload-file.click-here-to-upload-files')}
                  </Typography>
                </Box>
              </ButtonBase>
            ) : (
              <Button
                startIcon={<Upload />}
                onClick={() => setOpen(true)}
                variant="outlined"
                sx={{ mt: 2, mb: 1 }}
              >
                {t('upload-file.upload-files')}
              </Button>
            )}
          </>
        )}
      </Box>

      {error && (
        <Typography variant="body2" margin="5px" color="error">
          {error}
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent>
          <Box sx={{ width: '100%' }} {...rest}>
            <DropZoneStyle
              {...getRootProps()}
              sx={{
                ...(isDragActive && { opacity: 0.72 }),
                ...((isDragReject || error) && {
                  borderColor: 'primary.light',
                  bgcolor: 'primary.lighter',
                }),
              }}
            >
              <input {...getInputProps()} />

              <Box
                component="img"
                alt={t('form.select-file-alt')}
                src="/static/illustrations/illustration_upload.svg"
                sx={{ height: 160 }}
              />

              <Box
                sx={{
                  p: 3,
                  ml: { md: 2 },
                }}
              >
                <Typography color="text.primary" gutterBottom variant="h5">
                  {t('upload-file.drop-or-select-file')}
                </Typography>

                {label ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {label}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('upload-file.drop-file-here-or')}&nbsp;
                    <Link underline="always">{t('upload-file.browse')}</Link>
                    &nbsp;
                    {t('upload-file.thorough-your-machine')}
                  </Typography>
                )}
              </Box>
            </DropZoneStyle>

            <List disablePadding sx={{ ...(hasFile && { my: 5 }) }}>
              <AnimatePresence>
                {hasFile && (
                  <ListItem
                    key={file.file.name}
                    component={motion.div}
                    {...varFadeInRight}
                    sx={{
                      my: 1,
                      py: 0.5,
                      px: 2,
                      borderRadius: 1,
                      border: (theme) => `solid 1px ${theme.palette.divider}`,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <ListItemIcon>
                      <Icon icon={fileFill} width={32} height={32} />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.file.name}
                      secondary={fData(file.file.size)}
                      primaryTypographyProps={{ variant: 'subtitle2' }}
                    />
                    <ListItemSecondaryAction>
                      <MIconButton
                        edge="end"
                        size="small"
                        onClick={handleRemoveFile}
                      >
                        <Icon icon={closeFill} />
                      </MIconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              </AnimatePresence>
            </List>

            {hasFile && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  '& > *': { ml: 1.5 },
                }}
              >
                <Button onClick={() => setFile()} sx={{ mr: 1.5 }}>
                  {t('upload-file.remove')}
                </Button>
                <Button variant="contained" onClick={() => setOpen(false)}>
                  {t('upload-file.add')}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const uploadFileOnChange = onChangeIdentity;

export function UploadFileWithoutDialog({
  label,
  value: file,
  onChange: setFile,
  error,
  accept,
  maxSize,
  onDropRejected = _noop,
}) {
  const { t } = useTranslation('translation');
  const hasFile = !!file;

  const handleDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const dataUri = await readFile(file);
    setFile({ file, data: file, dataUri });
  };

  const handleRemoveFile = () => setFile(null);

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept,
      multiple: false,
      onDrop: handleDrop,
      maxSize,
      onDropRejected,
    });

  return (
    <>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
        }}
      >
        <input {...getInputProps()} />

        <Box
          component="img"
          alt={t('form.select-file-alt')}
          src="/static/illustrations/illustration_upload.svg"
          sx={{ height: 160 }}
        />

        <Box sx={{ p: 3, ml: { md: 2 } }}>
          <Typography gutterBottom variant="h5">
            {t('upload-file.drop-or-select-file')}
          </Typography>

          {label ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {label}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('upload-file.drop-file-here-or')}&nbsp;
              <Link underline="always">{t('upload-file.browse')}</Link>
              &nbsp;{t('upload-file.thorough-your-machine')}
            </Typography>
          )}
        </Box>
      </DropZoneStyle>

      <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
        <AnimatePresence>
          {hasFile && (
            <ListItem
              component={motion.div}
              {...varFadeInRight}
              sx={{
                my: 1,
                py: 0.5,
                px: 2,
                borderRadius: 1,
                border: (theme) => `solid 1px ${theme.palette.divider}`,
                bgcolor: 'background.paper',
              }}
            >
              <ListItemIcon>
                <Icon icon={fileFill} width={32} height={32} />
              </ListItemIcon>
              <ListItemText
                primary={file.file.name}
                secondary={fData(file.file.size)}
                primaryTypographyProps={{ variant: 'subtitle2' }}
              />
              <ListItemSecondaryAction>
                <MIconButton edge="end" size="small" onClick={handleRemoveFile}>
                  <Icon icon={closeFill} />
                </MIconButton>
              </ListItemSecondaryAction>
            </ListItem>
          )}
        </AnimatePresence>
      </List>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </>
  );
}

export const SelectComponent = ({
  items = [],
  displayRefresh = false,
  ...props
}) => {
  const restProps = { ...props };
  delete restProps.key;
  const getItems = (items) => {
    if (typeof items === 'function') {
      return items();
    }
    return items;
  };

  return (
    <Box
      width={'100%'}
      display="flex"
      alignItems="center"
      justifyContent={'center'}
    >
      <TextField
        SelectProps={{
          endAdornment: restProps.hasClear ? (
            <IconButton
              sx={{
                visibility: !isEmpty(restProps.value) ? 'visible' : 'hidden',
              }}
              onClick={(e) =>
                restProps.onChange({ ...e, target: { value: '' } })
              }
            >
              <ClearIcon />
            </IconButton>
          ) : (
            ''
          ),
        }}
        {...restProps}
        select
        helperText={restProps.error}
      >
        {getItems(items).map(({ value, title, disabled, key }, index) => (
          <MenuItem
            key={key || value || index}
            value={value}
            disabled={!!disabled}
          >
            {title}
          </MenuItem>
        ))}
      </TextField>
      {displayRefresh ? (
        <Button onClick={() => restProps.onRefresh?.()}>
          <Sync />
        </Button>
      ) : null}
    </Box>
  );
};

const renderOptionForNearByOrgs = (props, option) => {
  const { key, ...optionProps } = props;
  if (option.more_hint) {
    return (
      <RecipientMoreHint key={key} {...optionProps} label={option.more_hint} />
    );
  }
  return (
    <Box
      key={key}
      {...optionProps}
      display="flex"
      flexDirection="column"
      mb={1}
    >
      <Typography variant="body1" alignSelf={'flex-start'}>
        {option.animal_food_rescuer_type ? (
          <Pets fontSize="small" sx={{ mr: 1 }} />
        ) : null}
        {option.title}
      </Typography>
      <Typography
        color="text.secondary"
        variant="overline"
        alignSelf={'flex-start'}
        fontSize={12}
      >
        {option.formatted_address || option.email}
      </Typography>
      <RecipientMatchCaption labels={option.match_labels} />
      {option.tax_exempt_status ? (
        <Box alignSelf="flex-start" mt={0.5}>
          <TaxExemptBadge status={option.tax_exempt_status} />
        </Box>
      ) : null}
      {option.pending_label ? (
        <Typography
          color="warning.main"
          variant="caption"
          alignSelf="flex-start"
        >
          {option.pending_label}
        </Typography>
      ) : null}
    </Box>
  );
};

export const AutoCompleteComponent = ({
  items = [],
  displayRefresh = false,
  renderOption = renderOptionForNearByOrgs,
  ...props
}) => {
  const getItems = (items) => {
    if (typeof items === 'function') {
      return items();
    }
    return items;
  };

  const options = getItems(items);

  return (
    <Box
      width={'100%'}
      display="flex"
      alignItems="center"
      justifyContent={'center'}
      my={3}
    >
      <MUIAutocomplete
        fullWidth
        options={options}
        loading={props.loading}
        groupBy={props.groupBy}
        renderGroup={props.renderGroup}
        filterOptions={props.filterOptions}
        getOptionLabel={(option) => option.title || ''}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={options.find((opt) => opt.value === props.value) || null}
        onChange={(_, newValue) => {
          const event = {
            ...props,
            target: {
              name: props?.name,
              value: newValue ? newValue.value : '',
            },
          };
          if (props.onChange) {
            props.onChange(event);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            error={!!props.error}
            helperText={props.error || props.helperText}
            FormHelperTextProps={
              !props.error && props.helperTextColor
                ? { sx: { color: props.helperTextColor } }
                : undefined
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {props.loading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            fullWidth
          />
        )}
        renderOption={renderOption}
        disabled={props.disabled}
        getOptionDisabled={(option) => option.disabled}
      />
      {displayRefresh ? (
        <Button onClick={() => props.onRefresh?.()}>
          <Sync />
        </Button>
      ) : null}
    </Box>
  );
};

// Yup drops tests chained after a when() whose branch returns a fresh schema, so apply this inside the branch.
export function requireAtLeastOneFoodType(schema, message) {
  return schema.test(
    'at-least-one-property',
    message,
    (value) => Object.entries(processFoodTypes(value)).length > 0
  );
}

// A blank or non-numeric weight casts to NaN; ask for a weight instead of
// Yup's default "must be a `number` type" message.
export const foodTypeWeightSchema = (t) =>
  Yup.number()
    .typeError(t('donations.enter-weight-to-continue'))
    .nonNullable(t('donations.enter-weight-to-continue'));

// A cleared or half-typed weight field is blank/NaN while the form validates
// on each change; Weight throws on NaN, so count it as 0. The field's own
// number validation (foodTypeWeightSchema) still flags it.
const toWeightNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function processFoodTypes(foodTypes) {
  let foodWeights = {};

  for (const [type, weight] of Object.entries(foodTypes)) {
    const isPartial = !!(
      weight?.assignedRecipients ||
      weight?.minReservableWeight ||
      weight?.maxReservableWeight
    );
    if (
      isPartial
        ? weight?.assignedRecipients?.length > 0 ||
          weight?.minReservableWeight >= 0 ||
          weight?.maxReservableWeight >= 0 ||
          weight?.value >= 0
        : weight?.value >= 0
    ) {
      foodWeights[type] = {
        value: new Weight(toWeightNumber(weight.value), weight.unit).as('lbs'),
        unit: weight.unit,
        ...(isPartial
          ? {
              minReservableWeight: new Weight(
                toWeightNumber(weight.minReservableWeight),
                weight.unit
              ).as('lbs'),
              maxReservableWeight: new Weight(
                toWeightNumber(weight.maxReservableWeight),
                weight.unit
              ).as('lbs'),
              assignedRecipients: weight.assignedRecipients
                .filter((recipient) => recipient.teamId)
                .map((recipient) => ({
                  teamId: recipient.teamId,
                  reservedWeight: new Weight(
                    toWeightNumber(recipient.reservedWeight),
                    weight.unit
                  ).as('lbs'),
                  rescueId: recipient.rescueId,
                  patternRescueId: recipient.patternRescueId,
                })),
            }
          : {}),
      };
    }
  }

  return foodWeights;
}

/**
 * Build the donation write fields the backend requires, from form
 * state. Called by the form/component that submits the request:
 *
 *   { isPartial, items: [{ foodType, estimatedWeight, unit, minClaimWeight,
 *     maxClaimWeight }], recipients: [{ teamId, destAddress,
 *     items: [{ foodType, weight, rescueId?, patternRescueId? }] }] }
 *
 * Weights are converted to lbs via processFoodTypes; `unit` stays the display
 * unit. A regular donation has 0..1 recipients claiming everything; partial
 * recipients carry their per-food-type reservations. The destination is a
 * property of the recipient.
 */
export function buildDonationWriteFields({
  foodTypes,
  isPartialRescue,
  receiverTeamId = null,
  destAddress = null,
  destinationAddresses = [],
}) {
  const weights = processFoodTypes(foodTypes);
  const isPartial = !!isPartialRescue;
  const singleDest =
    destAddress && Object.keys(destAddress).length > 0 ? destAddress : null;

  const items = Object.entries(weights).map(([foodType, weightData]) => ({
    foodType,
    estimatedWeight: weightData.value ?? null,
    unit: weightData.unit || 'lbs',
    minClaimWeight: weightData.minReservableWeight ?? null,
    maxClaimWeight: weightData.maxReservableWeight ?? null,
  }));

  const destByTeam = new Map(
    (destinationAddresses || []).map((entry) => [entry.teamId, entry.location])
  );

  let recipients = [];
  if (isPartial) {
    const itemsByTeam = new Map();
    for (const [foodType, weightData] of Object.entries(weights)) {
      for (const recipient of weightData.assignedRecipients || []) {
        if (!recipient.teamId || recipient.teamId === 'unassigned') {
          continue;
        }
        if (!itemsByTeam.has(recipient.teamId)) {
          itemsByTeam.set(recipient.teamId, []);
        }
        itemsByTeam.get(recipient.teamId).push({
          foodType,
          weight: recipient.reservedWeight,
          ...(recipient.rescueId ? { rescueId: recipient.rescueId } : {}),
          ...(recipient.patternRescueId
            ? { patternRescueId: recipient.patternRescueId }
            : {}),
        });
      }
    }
    recipients = [...itemsByTeam.entries()].map(([teamId, teamItems]) => ({
      teamId,
      destAddress: destByTeam.get(teamId) ?? null,
      items: teamItems,
    }));
  } else if (receiverTeamId) {
    recipients = [{ teamId: receiverTeamId, destAddress: singleDest }];
  }

  return { isPartial, items, recipients };
}

export const DayOfTheMonthSelector = ({ value, ...params }) => {
  const { t } = useTranslation('translation');
  const type = useMemo(() => {
    if (value.isLastDay) {
      return 'last';
    }

    return _isFinite(_get(value, 'week.dayOfTheWeek'))
      ? 'day_of_the_week'
      : 'custom';
  }, [value]);
  return (
    <>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('donations.select-day-of-the-month-to-repeat')}
      </Typography>
      <RadioGroup
        value={type}
        onChange={(event) => {
          const inpValue = event.target.value;
          if (inpValue === 'last') {
            params.onChange({
              isLastDay: true,
              customDay: null,
              week: {
                number: null,
                dayOfTheWeek: null,
              },
            });
          } else if (inpValue === 'day_of_the_week') {
            params.onChange({
              isLastDay: false,
              customDay: null,
              week: {
                number: 1,
                dayOfTheWeek: 1,
              },
            });
          } else {
            params.onChange({
              isLastDay: false,
              customDay: null,
              week: {
                number: null,
                dayOfTheWeek: null,
              },
            });
          }
        }}
      >
        <FormControlLabel
          value="last"
          control={<Radio />}
          label={t('donations.last-day-of-the-month')}
        />
        <Box>
          <FormControlLabel
            value="day_of_the_week"
            control={<Radio />}
            label={t('donations.select-day-of-the-week')}
          />
          <Box display="flex" flexDirection={'column'}>
            <Box display="flex">
              <FormControl
                disabled={type !== 'day_of_the_week'}
                margin="normal"
                style={{ width: 200, marginLeft: 20, marginRight: 20 }}
              >
                <InputLabel
                  id="no-of-the-week"
                  shrink={_isFinite(_get(value, 'week.number'))}
                >
                  {t('donations.no-of-the-week')}
                </InputLabel>
                <Select
                  labelId="no-of-the-week"
                  id="no-of-the-week"
                  value={_get(value, 'week.number')}
                  label={t('donations.no-of-the-week')}
                  onChange={(e) => {
                    params.onChange({
                      isLastDay: false,
                      week: {
                        ..._get(value, 'week', {}),
                        number: e.target.value,
                      },
                    });
                  }}
                >
                  <MenuItem key={1} value={1}>
                    {t('donations.1st')}
                  </MenuItem>
                  <MenuItem key={2} value={2}>
                    {t('donations.2nd')}
                  </MenuItem>
                  <MenuItem key={3} value={3}>
                    {t('donations.3rd')}
                  </MenuItem>
                  <MenuItem key={4} value={4}>
                    {t('donations.4th')}
                  </MenuItem>
                  <MenuItem key={5} value={5}>
                    {t('donations.5th')}
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl
                disabled={type !== 'day_of_the_week'}
                margin="normal"
                style={{ width: 200 }}
              >
                <InputLabel
                  id="day-of-the-week"
                  shrink={_isFinite(_get(value, 'week.dayOfTheWeek'))}
                >
                  {t('donations.day-of-the-week')}
                </InputLabel>
                <Select
                  labelId="day-of-the-week"
                  id="day-of-the-week"
                  value={_get(value, 'week.dayOfTheWeek')}
                  label={t('donations.day-of-the-week')}
                  onChange={(e) =>
                    params.onChange({
                      isLastDay: false,
                      week: {
                        ..._get(value, 'week', {}),
                        dayOfTheWeek: e.target.value,
                      },
                    })
                  }
                >
                  <MenuItem key={0} value={0}>
                    {t('donations.Sunday')}
                  </MenuItem>
                  <MenuItem key={1} value={1}>
                    {t('donations.Monday')}
                  </MenuItem>
                  <MenuItem key={2} value={2}>
                    {t('donations.Tuesday')}
                  </MenuItem>
                  <MenuItem key={3} value={3}>
                    {t('donations.Wednesday')}
                  </MenuItem>
                  <MenuItem key={4} value={4}>
                    {t('donations.Thursday')}
                  </MenuItem>
                  <MenuItem key={5} value={5}>
                    {t('donations.Friday')}
                  </MenuItem>
                  <MenuItem key={6} value={6}>
                    {t('donations.Saturday')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
        <Box>
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label={t('donations.select-any-day-of-the-month')}
          />
          <Box style={{ marginLeft: 20, marginRight: 20 }}>
            <FormControl
              disabled={type !== 'custom'}
              margin="normal"
              style={{ width: 200 }}
            >
              <InputLabel id="day-of-the-month" shrink={value.customDay}>
                {t('donations.day-of-the-month')}
              </InputLabel>
              <Select
                labelId="day-of-the-month"
                id="day-of-the-month"
                value={value.customDay}
                label={t('donations.day-of-the-month')}
                onChange={(e) =>
                  params.onChange({
                    isLastDay: false,
                    customDay: e.target.value,
                    week: {
                      number: null,
                      dayOfTheWeek: null,
                    },
                  })
                }
              >
                {_range(1, 32).map((i) => (
                  <MenuItem value={i} key={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </RadioGroup>
      <Typography color="error" variant="caption" margin="10px">
        {params.error}
      </Typography>
    </>
  );
};
